'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect, useState } from 'react'

// Literal English fallback copy — see the note on GlobalError below for why this boundary
// deliberately stays untranslated. Hoisted to module scope so it isn't a JSX literal.
const ERROR_TITLE = 'Oops!'
const RETRY_LABEL = 'Try again'

const errorMessages = [
  "Looks like the waiter spilled a drink by accident. We're cleaning it up now.",
  "The director called 'cut!' a bit too early. We're fixing the scene.",
  "Someone tripped over a cable backstage. We're untangling things now.",
  "The film got stuck in the projector. We're threading it back through.",
  "A light bulb burned out mid-scene. We're replacing it now.",
  "The script got mixed up. We're sorting the pages back in order.",
]

// This boundary replaces the entire app tree (it renders its own <html>/<body>), so it is
// NOT wrapped by NextIntlClientProvider — calling useExtracted() here would throw while
// handling the original error. Fallback copy is kept as literals on purpose.
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [message] = useState(() => errorMessages[Math.floor(Math.random() * errorMessages.length)])

  useEffect(() => {
    Sentry.captureException(error, { tags: { source: 'global-error-boundary' } })
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0a',
          color: '#fafafa',
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '2rem',
            maxWidth: '28rem',
          }}
        >
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 600,
              margin: '0 0 1rem 0',
              letterSpacing: '-0.02em',
            }}
          >
            {ERROR_TITLE}
          </h1>
          <p
            style={{
              fontSize: '1rem',
              color: '#a3a3a3',
              margin: '0 0 2rem 0',
              lineHeight: 1.6,
            }}
          >
            {message}
          </p>
          <button
            onClick={() => reset()}
            style={{
              backgroundColor: '#fafafa',
              color: '#171717',
              border: 'none',
              borderRadius: '0.375rem',
              padding: '0.625rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'opacity 0.15s ease',
            }}
            onMouseOver={e => {
              e.currentTarget.style.opacity = '0.9'
            }}
            onMouseOut={e => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            {RETRY_LABEL}
          </button>
        </div>
      </body>
    </html>
  )
}
